import React, { useState, useMemo } from 'react';
import {
  Trash2, Edit, Search, ChevronUp, ChevronDown, ChevronsUpDown,
  LayoutGrid, List, Table2, Eye, ZoomIn, ImageOff
} from 'lucide-react';

const BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || 'http://localhost:5000';

/* Resolve image src */
const toSrc = (v) => {
  if (!v) return null;
  if (typeof v !== 'string') return null;
  if (v.startsWith('http')) return v;
  if (v.startsWith('/uploads/')) return `${BASE}${v}`;
  return null;
};

/* ── Hover-zoom image ── */
function ZoomImage({ src, alt = '', size = 'md' }) {
  const [hovered, setHovered] = useState(false);
  const resolved = toSrc(src) || src;

  const sizeClass = {
    sm:  'w-12 h-12',
    md:  'w-20 h-20',
    lg:  'w-full h-52',
    card:'w-full h-48',
  }[size] || 'w-16 h-16';

  if (!resolved) {
    return (
      <div className={`${sizeClass} rounded-xl bg-gray-100 flex items-center justify-center shrink-0`}>
        <ImageOff size={16} className="text-gray-300" />
      </div>
    );
  }

  return (
    <div className={`relative ${size === 'card' || size === 'lg' ? 'w-full' : 'shrink-0'} group/zoom`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}>
      <div className={`${sizeClass} rounded-xl overflow-hidden bg-gray-100 cursor-zoom-in`}>
        <img src={resolved} alt={alt}
          className="w-full h-full object-cover transition-transform duration-300 group-hover/zoom:scale-110" />
      </div>
      {/* Zoom icon overlay */}
      <div className="absolute inset-0 rounded-xl bg-black/0 group-hover/zoom:bg-black/20 transition-colors duration-200 flex items-center justify-center pointer-events-none">
        <ZoomIn size={16} className="text-white opacity-0 group-hover/zoom:opacity-100 transition-opacity duration-200" />
      </div>
      {/* Zoomed popup */}
      {hovered && (
        <div className="absolute left-full top-0 ml-3 z-[999] pointer-events-none"
          style={{ filter: 'drop-shadow(0 8px 24px rgba(0,0,0,0.25))' }}>
          <img src={resolved} alt={alt}
            className="w-56 h-56 object-cover rounded-2xl border-2 border-white" />
        </div>
      )}
    </div>
  );
}

/* ── sort icon ── */
function SortIcon({ dir }) {
  if (dir === 'asc')  return <ChevronUp size={12} className="text-yellow-500" />;
  if (dir === 'desc') return <ChevronDown size={12} className="text-yellow-500" />;
  return <ChevronsUpDown size={12} className="text-gray-300" />;
}

/* ── summary bar ── */
function SummaryBar({ data, summaryFields }) {
  if (!summaryFields?.length) return null;
  return (
    <div className="flex flex-wrap gap-3 mb-4">
      {summaryFields.map(({ key, label, color, render }) => {
        const val = render ? render(data) : data.filter(r => r[key]).length;
        return (
          <div key={key} className="flex items-center gap-2 bg-white border border-gray-100 rounded-xl px-4 py-2 shadow-sm">
            <span className="w-2.5 h-2.5 rounded-full" style={{ background: color || '#B8A830' }} />
            <span className="text-xs text-gray-500">{label}</span>
            <span className="text-sm font-bold text-gray-800">{val}</span>
          </div>
        );
      })}
    </div>
  );
}

/* ── card view ── */
function CardView({ columns, data, onEdit, onDelete, onView }) {
  const imgCol   = columns.find(c => c.isImage);
  const titleCol = columns.find(c => c.isTitle) || columns[1];
  const rest     = columns.filter(c => !c.isImage && c !== titleCol);

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5 p-5">
      {data.map((row, i) => {
        const imgSrc = imgCol ? (typeof row[imgCol.key] === 'string' ? row[imgCol.key]
          : Array.isArray(row[imgCol.key]) ? row[imgCol.key][0] : null) : null;
        return (
          <div key={row._id || i}
            className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-all duration-300 group flex flex-col">
            {/* Image area — tall, with zoom */}
            <div className="relative h-52 bg-gray-50 overflow-hidden">
              {imgSrc ? (
                <>
                  <img src={toSrc(imgSrc) || imgSrc} alt=""
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-colors duration-300 flex items-center justify-center">
                    <ZoomIn size={24} className="text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 drop-shadow-lg" />
                  </div>
                </>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center text-gray-200 gap-2">
                  <ImageOff size={32} />
                  <span className="text-xs text-gray-300">No image</span>
                </div>
              )}
            </div>

            <div className="p-4 flex-1 flex flex-col">
              <p className="font-semibold text-gray-800 text-sm truncate mb-3">
                {titleCol?.render ? titleCol.render(row[titleCol.key], row) : (row[titleCol?.key] || '—')}
              </p>
              <div className="space-y-1.5 mb-4 flex-1">
                {rest.slice(0, 4).map(col => (
                  <div key={col.key} className="flex items-center justify-between text-xs">
                    <span className="text-gray-400 font-medium">{col.label}</span>
                    <span className="text-gray-600 font-medium text-right max-w-[60%] truncate">
                      {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                    </span>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2 pt-3 border-t border-gray-50">
                {onView && (
                  <button onClick={() => onView(row)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-xs text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-xl transition-colors font-medium">
                    <Eye size={12} /> View
                  </button>
                )}
                {onEdit && (
                  <button onClick={() => onEdit(row)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-xs text-gray-500 hover:text-yellow-600 hover:bg-yellow-50 rounded-xl transition-colors font-medium">
                    <Edit size={12} /> Edit
                  </button>
                )}
                {onDelete && (
                  <button onClick={() => onDelete(row._id)}
                    className="flex-1 flex items-center justify-center gap-1 py-2 text-xs text-gray-500 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors font-medium">
                    <Trash2 size={12} /> Delete
                  </button>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── compact list view ── */
function ListView({ columns, data, onEdit, onDelete, onView }) {
  const imgCol   = columns.find(c => c.isImage);
  const titleCol = columns.find(c => c.isTitle) || columns[1];
  const subCol   = columns[2];

  return (
    <div className="divide-y divide-gray-50">
      {data.map((row, i) => {
        const imgSrc = imgCol ? (typeof row[imgCol.key] === 'string' ? row[imgCol.key]
          : Array.isArray(row[imgCol.key]) ? row[imgCol.key][0] : null) : null;
        return (
          <div key={row._id || i}
            className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50 transition-colors group">
            {imgCol && (
              <ZoomImage src={imgSrc} size="md" />
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-gray-800 truncate">
                {titleCol?.render ? titleCol.render(row[titleCol.key], row) : (row[titleCol?.key] || '—')}
              </p>
              {subCol && (
                <p className="text-xs text-gray-400 truncate mt-0.5">
                  {subCol.render ? subCol.render(row[subCol.key], row) : (row[subCol.key] ?? '')}
                </p>
              )}
            </div>
            <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {onView && (
                <button onClick={() => onView(row)} className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-colors">
                  <Eye size={15} />
                </button>
              )}
              {onEdit && (
                <button onClick={() => onEdit(row)} className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-xl transition-colors">
                  <Edit size={15} />
                </button>
              )}
              {onDelete && (
                <button onClick={() => onDelete(row._id)} className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors">
                  <Trash2 size={15} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ── table view ── */
function TableView({ columns, data, onEdit, onDelete, onView, sortKey, sortDir, onSort }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-100">
            {columns.map(col => (
              <th key={col.key}
                onClick={() => col.sortable !== false && onSort(col.key)}
                className={`text-left py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider select-none
                  ${col.sortable !== false ? 'cursor-pointer hover:text-gray-800' : ''}`}>
                <div className="flex items-center gap-1">
                  {col.label}
                  {col.sortable !== false && <SortIcon dir={sortKey === col.key ? sortDir : null} />}
                </div>
              </th>
            ))}
            <th className="text-right py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {data.map((row, i) => (
            <tr key={row._id || i} className="hover:bg-gray-50 transition-colors group">
              {columns.map(col => {
                // Special handling for image columns — use ZoomImage
                if (col.isImage) {
                  const rawVal = row[col.key];
                  const imgSrc = typeof rawVal === 'string' ? rawVal
                    : Array.isArray(rawVal) ? rawVal[0] : null;
                  return (
                    <td key={col.key} className="py-3 px-4">
                      <ZoomImage src={imgSrc} size="md" />
                    </td>
                  );
                }
                return (
                  <td key={col.key} className="py-3 px-4 text-gray-600">
                    {col.render ? col.render(row[col.key], row) : (row[col.key] ?? '—')}
                  </td>
                );
              })}
              <td className="py-3 px-4">
                <div className="flex items-center justify-end gap-1">
                  {onView && (
                    <button onClick={() => onView(row)}
                      className="p-2 text-gray-300 hover:text-blue-500 hover:bg-blue-50 rounded-xl transition-colors opacity-0 group-hover:opacity-100">
                      <Eye size={15} />
                    </button>
                  )}
                  {onEdit && (
                    <button onClick={() => onEdit(row)}
                      className="p-2 text-gray-300 hover:text-yellow-600 hover:bg-yellow-50 rounded-xl transition-colors opacity-0 group-hover:opacity-100">
                      <Edit size={15} />
                    </button>
                  )}
                  {onDelete && (
                    <button onClick={() => onDelete(row._id)}
                      className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl transition-colors opacity-0 group-hover:opacity-100">
                      <Trash2 size={15} />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {data.length === 0 && (
        <div className="flex flex-col items-center justify-center py-16 text-gray-300 gap-2">
          <Table2 size={32} />
          <p className="text-sm">No records found</p>
        </div>
      )}
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════════════════
   Main DataTable component
   ══════════════════════════════════════════════════════════════════════════ */
export default function DataTable({
  columns,
  data,
  onEdit,
  onDelete,
  onView,
  searchKeys,
  summaryFields,
  defaultView = 'table',
  title,
  subtitle,
  actions,
}) {
  const [view, setView]       = useState(defaultView);
  const [search, setSearch]   = useState('');
  const [sortKey, setSortKey] = useState('');
  const [sortDir, setSortDir] = useState('asc');

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const filtered = useMemo(() => {
    let rows = [...data];
    if (search.trim() && searchKeys?.length) {
      const q = search.toLowerCase();
      rows = rows.filter(r =>
        searchKeys.some(k => String(r[k] ?? '').toLowerCase().includes(q))
      );
    }
    if (sortKey) {
      rows.sort((a, b) => {
        const av = a[sortKey] ?? '';
        const bv = b[sortKey] ?? '';
        const cmp = String(av).localeCompare(String(bv), undefined, { numeric: true });
        return sortDir === 'asc' ? cmp : -cmp;
      });
    }
    return rows;
  }, [data, search, sortKey, sortDir, searchKeys]);

  const viewBtns = [
    { id: 'table', icon: Table2,     tip: 'Table view' },
    { id: 'list',  icon: List,       tip: 'List view' },
    { id: 'card',  icon: LayoutGrid, tip: 'Card view' },
  ];

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-visible">
      {/* header */}
      <div className="px-5 py-4 border-b border-gray-50 flex flex-wrap items-center gap-3">
        {(title || subtitle) && (
          <div className="mr-auto">
            {title    && <p className="text-sm font-semibold text-gray-800">{title}</p>}
            {subtitle && <p className="text-xs text-gray-400">{subtitle}</p>}
          </div>
        )}

        {/* search */}
        {searchKeys?.length > 0 && (
          <div className="relative">
            <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className="pl-8 pr-3 py-1.5 text-xs border border-gray-200 rounded-xl focus:outline-none focus:border-yellow-400 w-44 transition-colors"
            />
          </div>
        )}

        {/* view toggle */}
        <div className="flex items-center gap-0.5 bg-gray-100 rounded-xl p-0.5">
          {viewBtns.map(({ id, icon: Icon, tip }) => (
            <button key={id} onClick={() => setView(id)} title={tip}
              className={`p-1.5 rounded-lg transition-colors ${view === id ? 'bg-white shadow-sm text-yellow-600' : 'text-gray-400 hover:text-gray-600'}`}>
              <Icon size={14} />
            </button>
          ))}
        </div>

        {actions}
      </div>

      {/* summary */}
      {summaryFields?.length > 0 && (
        <div className="px-5 pt-4">
          <SummaryBar data={data} summaryFields={summaryFields} />
        </div>
      )}

      {/* results count */}
      {search && (
        <div className="px-5 py-2 text-xs text-gray-400">
          {filtered.length} result{filtered.length !== 1 ? 's' : ''} for "<span className="text-gray-600">{search}</span>"
        </div>
      )}

      {/* content */}
      {view === 'table' && (
        <TableView columns={columns} data={filtered} onEdit={onEdit} onDelete={onDelete} onView={onView}
          sortKey={sortKey} sortDir={sortDir} onSort={handleSort} />
      )}
      {view === 'list' && (
        <ListView columns={columns} data={filtered} onEdit={onEdit} onDelete={onDelete} onView={onView} />
      )}
      {view === 'card' && (
        <CardView columns={columns} data={filtered} onEdit={onEdit} onDelete={onDelete} onView={onView} />
      )}
    </div>
  );
}
